/**
 * WorkDocMenuBar — Top menu bar with File, Edit, View, Insert, Format, Tools menus.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Save, FileDown, FileUp, Printer, Send, Search, FileType,
  Menu,
} from "lucide-react";

export default function WorkDocMenuBar({
  editor,
  onSave,
  onExport,
  onImport,
  onPrint,
  onDispatchToMail,
  onFindReplace,
  documentTitle,
}) {
  const [showMenu, setShowMenu] = useState(false);

  const menuItems = [
    {
      label: "File",
      items: [
        { label: "Save", icon: Save, shortcut: "Ctrl+S", action: onSave },
        { separator: true },
        { label: "Export as DOCX", icon: FileDown, action: () => onExport?.("docx") },
        { label: "Export as PDF", icon: FileDown, action: () => onExport?.("pdf") },
        { label: "Export as TXT", icon: FileDown, action: () => onExport?.("txt") },
        { label: "Export as MD", icon: FileDown, action: () => onExport?.("md") },
        { separator: true },
        { label: "Import DOCX", icon: FileUp, action: onImport },
        { separator: true },
        { label: "Print", icon: Printer, shortcut: "Ctrl+P", action: onPrint },
        { separator: true },
        { label: "Dispatch to Smart Mail", icon: Send, action: onDispatchToMail },
      ],
    },
    {
      label: "Edit",
      items: [
        { label: "Undo", shortcut: "Ctrl+Z", action: () => editor?.chain().focus().undo().run() },
        { label: "Redo", shortcut: "Ctrl+Y", action: () => editor?.chain().focus().redo().run() },
        { separator: true },
        { label: "Find & Replace", icon: Search, shortcut: "Ctrl+F", action: onFindReplace },
      ],
    },
    {
      label: "Insert",
      items: [
        { label: "Table (3x3)", action: () => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
        { label: "Image", action: () => {
          const url = prompt("Enter image URL:");
          if (url) editor?.chain().focus().setImage({ src: url }).run();
        }},
        { label: "Link", action: () => {
          const url = prompt("Enter URL:");
          if (url) editor?.chain().focus().setLink({ href: url }).run();
        }},
        { label: "Horizontal Rule", action: () => editor?.chain().focus().setHorizontalRule().run() },
      ],
    },
    {
      label: "Format",
      items: [
        { label: "Clear Formatting", action: () => editor?.chain().focus().clearNodes().unsetAllMarks().run() },
        { separator: true },
        { label: "UPPERCASE", action: () => {
          const { from, to } = editor?.state.selection;
          const text = editor?.state.doc.textBetween(from, to);
          if (text) editor?.chain().focus().deleteRange({ from, to }).insertContent(text.toUpperCase()).run();
        }},
        { label: "lowercase", action: () => {
          const { from, to } = editor?.state.selection;
          const text = editor?.state.doc.textBetween(from, to);
          if (text) editor?.chain().focus().deleteRange({ from, to }).insertContent(text.toLowerCase()).run();
        }},
        { label: "Title Case", action: () => {
          const { from, to } = editor?.state.selection;
          const text = editor?.state.doc.textBetween(from, to);
          if (text) editor?.chain().focus().deleteRange({ from, to }).insertContent(
            text.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          ).run();
        }},
      ],
    },
  ];

  return (
    <div className="flex items-center gap-1 px-3 py-1 bg-gray-900 border-b border-gray-700 text-xs text-gray-300">
      {menuItems.map((menu) => (
        <div key={menu.label} className="relative group">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-gray-300 hover:text-white hover:bg-gray-800 rounded">
            {menu.label}
          </Button>
          <div className="absolute top-full left-0 mt-0 hidden group-hover:block z-50 min-w-[200px] bg-gray-900 border border-gray-700 rounded shadow-xl">
            <div className="py-1">
              {menu.items.map((item, i) => {
                if (item.separator) return <div key={i} className="h-px bg-gray-700 my-1" />;
                const Icon = item.icon;
                return (
                  <button
                    key={i}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 hover:text-white"
                    onClick={() => { item.action?.(); }}
                  >
                    <span className="flex items-center gap-2">
                      {Icon && <Icon className="w-3.5 h-3.5" />}
                      {item.label}
                    </span>
                    {item.shortcut && <span className="text-gray-600 ml-4">{item.shortcut}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ))}

      <div className="flex-1" />

      {/* Quick action buttons */}
      <Button variant="ghost" size="sm" onClick={onSave} className="h-7 px-2 text-xs text-gray-400 hover:text-white" title="Save">
        <Save className="w-3.5 h-3.5 mr-1" /> Save
      </Button>
    </div>
  );
}
